/* Add a document to a project — drop/paste first, details second.

   Used inline inside the project Ask/Instruct card, and as the Add-document
   modal on the Documents tab / structure map. The submit button is only
   highlighted once a file is attached or pasted text is present. */
import React, { useId, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useToast } from './Toast';
import Modal from './Modal';
import { createEntity, updateEntity } from '../data/entities';
import { uploadFile, isUploadConfigured, safeFileSeg } from '../data/uploadService';
import { actorOf, appendAudit, DOC_TAG_GROUPS, docViewOf } from '../data/deals';
import DocTypePicker from './DocTypePicker';
import { expandDocumentFiles } from '../utils/expandDocumentFiles';
import './AddDocumentPortal.css';

// Importable document types. Folders and .zip archives are accepted too and
// expanded client-side into these (utils/expandDocumentFiles.js).
const ACCEPT = '.docx,.pdf,.txt,.rtf,.eml,.md,.xlsx,.xlsm,.zip';
const ACCEPT_RE = /\.(docx|pdf|txt|rtf|eml|md|xlsx|xlsm)$/i;

export function DocTagPicker({ value, onChange }) {
  const [custom, setCustom] = useState('');
  const selected = value || [];
  const toggle = (t) => onChange(selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);
  const addCustom = () => {
    const t = custom.trim().toLowerCase().replace(/\s+/g, '-').slice(0, 40);
    if (t && !selected.includes(t)) onChange([...selected, t]);
    setCustom('');
  };
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      {selected.length > 0 && (
        <span className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>
          {selected.map((t) => (
            <button key={t} type="button" className="cs-chip" onClick={() => toggle(t)} title="Remove tag">
              #{t} ×
            </button>
          ))}
        </span>
      )}
      {DOC_TAG_GROUPS.map((g) => {
        const remaining = g.tags.filter((t) => !selected.includes(t));
        if (!remaining.length) return null;
        return (
          <span key={g.label} className="u-cluster" style={{ '--gap': 'var(--sp-1)', alignItems: 'baseline' }}>
            <span className="u-xs u-muted" style={{ minWidth: 108 }}>{g.label}</span>
            {remaining.map((t) => (
              <button key={t} type="button" className="cs-chip u-muted" onClick={() => toggle(t)}>
                {t}
              </button>
            ))}
          </span>
        );
      })}
      <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        <input
          className="cs-input" style={{ maxWidth: 220 }} value={custom} placeholder="custom tag…"
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
        />
        {custom.trim() && (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={addCustom}>Add</button>
        )}
      </span>
    </div>
  );
}

export default function AddDocumentPortal({
  scope,
  deal,
  participant,
  defaultKind,
  defaultTitle,
  defaultTags,
  defaultCabinet,
  defaultDocType,
  compact = false,
  onClose,
  onDone,
}) {
  const { user } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uid = useId();
  const [title, setTitle] = useState(defaultTitle || '');
  const [kind, setKind] = useState(defaultKind || 'primary');
  // Catalog key (platform docTypes/ or this org's); '' = let smart-import decide.
  const [docType, setDocType] = useState(defaultDocType || '');
  const [docTypeEntry, setDocTypeEntry] = useState(null);
  // File cabinet: '' = working paper, 'input' = what came in, 'deliverable' = what goes out.
  const [cabinet, setCabinet] = useState(defaultCabinet || '');
  const [tags, setTags] = useState(defaultTags || []);
  const [pasted, setPasted] = useState('');
  const [files, setFiles] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [expanding, setExpanding] = useState(false);
  const [mode, setMode] = useState('file');
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const kindTouched = useRef(false);
  const [autoKind, setAutoKind] = useState(false);

  const ready = !expanding && (mode === 'paste' ? !!pasted.trim() : files.length > 0);

  const sniffKind = (list) => {
    if (kindTouched.current) return;
    const names = (list || []).map((f) => f.name);
    if (!names.length) return;
    if (names.every((n) => /articles|certificat|formation|filing|amendment|initial list|good standing|ein\b/i.test(n))) {
      setKind('filing');
      setTags((t) => [...new Set([...t, 'state-filing', 'certificate'])]);
      setAutoKind(true);
    }
  };

  // Accepts a FileList (picker / folder picker) or a DataTransfer (drop):
  // folders are walked and zips unpacked before filtering to ACCEPT_RE.
  const takeFiles = async (input) => {
    setExpanding(true);
    try {
      const { files: next, skipped: dropped } = await expandDocumentFiles(input, ACCEPT_RE);
      setFiles(next);
      setSkipped(dropped);
      sniffKind(next);
      if (!next.length && dropped.length) toast(`Nothing importable — ${dropped.length} file${dropped.length === 1 ? '' : 's'} skipped (unsupported type)`, 'danger');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setExpanding(false);
    }
  };

  // The directory-picker attributes are non-standard; React drops them, so
  // set them imperatively (same trick as pages/DropUpload.js).
  const bindFolderInput = (el) => {
    folderInputRef.current = el;
    if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); }
  };

  const reset = () => {
    setTitle(defaultTitle || '');
    setKind(defaultKind || 'primary');
    setDocType(defaultDocType || '');
    setDocTypeEntry(null);
    setCabinet(defaultCabinet || '');
    setTags(defaultTags || []);
    setPasted('');
    setFiles([]);
    setSkipped([]);
    setMode('file');
    setAutoKind(false);
    kindTouched.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // docType + the renderer family it maps to (docView). Without a pick the
  // worker fills both from smart-import; the renderer falls back on format.
  const typedFields = (format) => (docType
    ? { docType, docView: docTypeEntry?.view || docViewOf({ docType, format }) }
    : {});

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      const actor = actorOf(user, participant);
      if (mode === 'paste') {
        if (!pasted.trim()) throw new Error('Paste some content first');
        await createEntity('dealDoc', scope, {
          title: title.trim() || 'Pasted content',
          kind,
          ...typedFields('pasted'),
          ...(cabinet ? { cabinet } : {}),
          tags,
          format: 'pasted',
          pastedText: pasted,
          status: 'queued',
          createdBy: user.uid,
        });
      } else {
        if (!files.length) throw new Error('Choose a file first');
        if (!isUploadConfigured()) throw new Error('Upload backend not configured');
        const s3Prefix = (deal?.s3Prefix && String(deal.s3Prefix).startsWith('items/'))
          ? (deal.s3Prefix.endsWith('/') ? deal.s3Prefix : `${deal.s3Prefix}/`)
          : (deal?.id ? `items/${deal.id}/` : '');
        if (!s3Prefix) throw new Error('This project is missing an upload prefix');
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          const baseName = file.name.replace(/\.[^.]+$/, '');
          const safeName = safeFileSeg(file.name);
          const key = `${s3Prefix}source/${Date.now()}-${safeName}`;
          setProgress({ frac: 0, n: i + 1, total: files.length, name: file.name });
          // eslint-disable-next-line no-await-in-loop
          await uploadFile(file, { key, onProgress: (f) => setProgress({ frac: f, n: i + 1, total: files.length, name: file.name }) });
          const docTitle = files.length === 1
            ? (title.trim() || baseName)
            : (title.trim() ? `${title.trim()} — ${baseName}` : baseName);
          // eslint-disable-next-line no-await-in-loop
          await createEntity('dealDoc', scope, {
            title: docTitle,
            kind,
            ...typedFields(file.name.split('.').pop().toLowerCase()),
            ...(cabinet ? { cabinet } : {}),
            tags,
            format: file.name.split('.').pop().toLowerCase(),
            s3Key: key,
            status: 'queued',
            createdBy: user.uid,
          });
          // eslint-disable-next-line no-await-in-loop
          await appendAudit(scope, actor, 'doc.uploaded', `Document added: "${docTitle}"`);
        }
        // docCount = ALL docs in the project (the "N documents" chip) — every
        // kind counts, matching what the workspace actually lists.
        await updateEntity('deal', { org: scope.org }, scope.deal, { docCount: (deal.docCount || 0) + files.length });
        toast(files.length === 1 ? 'Document queued for import' : `${files.length} documents queued for import`, 'ok');
        reset();
        onDone?.();
        onClose?.();
        return;
      }
      await updateEntity('deal', { org: scope.org }, scope.deal, { docCount: (deal.docCount || 0) + 1 });
      await appendAudit(scope, actor, 'doc.uploaded', `Document added: "${title.trim() || 'untitled'}"`);
      toast('Document queued for import', 'ok');
      reset();
      onDone?.();
      onClose?.();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <form className="adp u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        <button
          type="button"
          className={`cs-btn cs-btn-sm ${mode === 'file' ? 'cs-btn-outline' : 'cs-btn-ghost'}`}
          onClick={() => setMode('file')}
        >
          Upload or drop
        </button>
        <button
          type="button"
          className={`cs-btn cs-btn-sm ${mode === 'paste' ? 'cs-btn-outline' : 'cs-btn-ghost'}`}
          onClick={() => setMode('paste')}
        >
          Paste text / email
        </button>
      </div>

      {mode === 'file' ? (
        <div className="cs-field">
          <input
            id={`${uid}-file`}
            ref={fileInputRef}
            type="file"
            multiple
            className="u-visually-hidden"
            accept={ACCEPT}
            onChange={(e) => takeFiles(e.target.files)}
          />
          <input
            id={`${uid}-folder`}
            ref={bindFolderInput}
            type="file"
            multiple
            className="u-visually-hidden"
            onChange={(e) => takeFiles(e.target.files)}
          />
          <div
            className={`cs-dropzone adp-drop${compact ? ' compact' : ''}${drag ? ' drag' : ''}${files.length ? ' has-files' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              takeFiles(e.dataTransfer);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
          >
            {expanding ? (
              <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
                <strong>Reading files…</strong>
                <span className="u-xs u-muted">Unpacking folders and archives</span>
              </div>
            ) : files.length ? (
              <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
                <strong>{files.length === 1 ? files[0].name : `${files.length} files ready`}</strong>
                {files.length > 1 && (
                  <span className="u-xs u-muted">{files.slice(0, 12).map((f) => f.name).join(' · ')}{files.length > 12 ? ` · +${files.length - 12} more` : ''}</span>
                )}
                {skipped.length > 0 && (
                  <span className="u-xs u-muted" title={skipped.join('\n')}>
                    {skipped.length} skipped (unsupported type)
                  </span>
                )}
                <span className="u-xs u-muted">Drop to replace, or click to choose again</span>
              </div>
            ) : (
              <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
                <strong>Drop files, a folder, or a .zip here — or click to choose</strong>
                <span className="u-xs">.docx, .pdf, .xlsx, .txt, .eml — each file becomes its own document</span>
              </div>
            )}
          </div>
          <button
            type="button"
            className="cs-btn cs-btn-ghost cs-btn-sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => folderInputRef.current?.click()}
          >
            Choose a folder…
          </button>
        </div>
      ) : (
        <div className="cs-field">
          <label htmlFor={`${uid}-paste`}>Email or document text</label>
          <textarea
            id={`${uid}-paste`}
            className="cs-input"
            rows={compact ? 6 : 8}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste an email thread or document text — it becomes its own document in this project"
          />
        </div>
      )}

      <div className="cs-field">
        <label htmlFor={`${uid}-title`}>Title</label>
        <input
          id={`${uid}-title`}
          className="cs-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Share Purchase Agreement — Working Draft"
        />
      </div>
      <div className="cs-field">
        <label htmlFor={`${uid}-kind`}>Type</label>
        <select
          id={`${uid}-kind`}
          className="cs-input"
          value={kind}
          onChange={(e) => { kindTouched.current = true; setAutoKind(false); setKind(e.target.value); }}
        >
          <option value="primary">Primary agreement</option>
          <option value="filing">Filing / certificate (articles, formation, amendments)</option>
          <option value="exhibit">Exhibit / schedule</option>
          <option value="prior">Prior version</option>
          <option value="email">Email thread</option>
          <option value="other">Other</option>
        </select>
        {kind === 'filing' && (
          <div className="cs-help">
            {autoKind && <strong>Auto-detected from the file name — change it if this is wrong. </strong>}
            Filed as evidence alongside the agreements it underpins.
          </div>
        )}
      </div>
      <div className="cs-field">
        <label htmlFor={`${uid}-doctype`}>Document type</label>
        <DocTypePicker
          inputId={`${uid}-doctype`}
          orgId={scope?.org}
          value={docType}
          onChange={(key, entry) => { setDocType(key); setDocTypeEntry(entry); }}
        />
        <div className="cs-help">
          What the document is — Side Letter, MSA, Board Resolution… Leave blank and smart import will classify it. Don't see yours? Type it and add it as a new type.
        </div>
      </div>
      <div className="cs-field">
        <label>Cabinet</label>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} role="radiogroup" aria-label="Cabinet">
          {[
            ['input', '📥 Input', 'What came in — data exports, invoices, hours files, received drafts'],
            ['', '📝 Working paper', 'Drafts, memos, analysis — the documents being worked'],
            ['deliverable', '📤 Deliverable', 'What goes out — the file to send, kept exactly as built'],
          ].map(([value, label, tip]) => (
            <button
              key={value || 'working'}
              type="button"
              role="radio"
              aria-checked={cabinet === value}
              className="cs-chip"
              style={cabinet === value ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
              data-tip={tip}
              onClick={() => setCabinet(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="cs-help">
          Where this file sits in the project's flow of paper — inputs and deliverables get their own cabinets at the top of the Documents tab.
        </div>
      </div>
      <div className="cs-field">
        <label>Tags</label>
        <DocTagPicker value={tags} onChange={setTags} />
        <div className="cs-help">
          Optional — e.g. <strong>original</strong> + <strong>source-template</strong>, or{' '}
          <strong>correspondence</strong> for a pasted email.
        </div>
      </div>

      {progress !== null && (
        <div role="status" className="u-small u-muted u-tabular">
          Uploading {progress.total > 1 ? `${progress.n}/${progress.total} — ` : ''}{progress.name}… {Math.round(progress.frac * 100)}%
        </div>
      )}
      <div className="u-cluster" style={{ justifyContent: onClose ? 'flex-end' : 'flex-start' }}>
        {onClose && (
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
        )}
        <button
          type="submit"
          className={`cs-btn ${ready ? 'cs-btn-primary' : 'cs-btn-outline'}`}
          disabled={!ready || busy}
          title={ready ? 'Import into this project' : 'Drop a file or paste text first'}
        >
          {busy ? 'Adding…' : 'Add & import'}
        </button>
      </div>
    </form>
  );
}

export function UploadDocModal({ scope, deal, participant, onClose, defaultKind, defaultTitle, defaultTags, defaultCabinet }) {
  return (
    <Modal title="Add document" onClose={onClose}>
      <AddDocumentPortal
        scope={scope}
        deal={deal}
        participant={participant}
        defaultKind={defaultKind}
        defaultTitle={defaultTitle}
        defaultTags={defaultTags}
        defaultCabinet={defaultCabinet}
        onClose={onClose}
      />
    </Modal>
  );
}
